import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { useBluelagoon } from "@bluelagoon";
import { useEffect, useState, useRef, useCallback } from "react";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import { DialogNewUser } from "./DialogNewUser";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@shadcn-in-nx/ui/alert-dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    Trash2,
    Shield,
    User,
    Building2,
    ChevronDown,
    ChevronRight,
    Search,
    ChevronLeft,
    ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Label } from "@shadcn-in-nx/ui/label";
import { useLocation } from "react-router-dom";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";
import { EyeOff } from "lucide-react";

const TableUsers = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const isAdminPanel = location.pathname.startsWith("/admin");
    const {
        user: currentUser,
        isAdmin,
        isSuperAdmin,
        isSupport,
        getUser,
        isFeatureEnabled,
    } = useAuth();
    const actAsAdmin = (isSuperAdmin || isSupport) && !isAdminPanel;
    const hasComplianceOfficer = isFeatureEnabled("complianceOfficer");
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [updatingRoleUserId, setUpdatingRoleUserId] = useState(null);
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const searchTimerRef = useRef(null);

    const {
        call: getUsers,
        response: users,
        isLoading,
        error,
    } = useBluelagoon({
        method: "getUsers",
    });

    const {
        call: deleteUser,
        isLoading: deleteLoading,
        error: deleteError,
    } = useBluelagoon({
        method: "deleteUser",
    });

    const {
        call: updateUserRole,
        isLoading: updateRoleLoading,
        error: updateRoleError,
    } = useBluelagoon({
        method: "updateUserRole",
    });

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    useEffect(() => {
        getUsers({
            search: debouncedSearch,
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
            companyOnly: actAsAdmin || undefined,
        }).catch((err) => {
            console.error("Failed to fetch users:", err);
        });
    }, [getUsers, debouncedSearch, currentPage, pageSize, actAsAdmin]);

    const handleUserCreated = () => {
        // Refetch users after new user is created
        getUsers({
            search: debouncedSearch,
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
            companyOnly: actAsAdmin || undefined,
        }).catch((err) => {
            console.error("Failed to fetch users:", err);
        });
    };

    const handleDeleteUser = async (userIdToDelete) => {
        setDeletingUserId(userIdToDelete);
        try {
            await deleteUser({ userIdToDelete });
            toast.success(t("users.userDeleted"));
            // Refetch users after deletion
            getUsers({
                search: debouncedSearch,
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                companyOnly: actAsAdmin || undefined,
            });
            await getUser();
        } catch (err) {
            console.error("Failed to delete user:", err);
            toast.error(err?.message || t("users.failedToDeleteUser"));
        } finally {
            setDeletingUserId(null);
        }
    };

    const handleRoleChange = async (userIdToUpdate, newRole) => {
        // Prevent users from changing their own role (handle type mismatches)
        if (
            !currentUser?.id ||
            String(userIdToUpdate) === String(currentUser.id)
        ) {
            toast.error(t("users.cannotChangeOwnRole"));
            return;
        }

        setUpdatingRoleUserId(userIdToUpdate);
        try {
            await updateUserRole({ userIdToUpdate, newRole });
            toast.success(t("users.userRoleUpdated"));
            // Refetch users after role update
            getUsers({
                search: debouncedSearch,
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                companyOnly: actAsAdmin || undefined,
            });
        } catch (err) {
            console.error("Failed to update user role:", err);
            toast.error(err?.message || t("users.failedToUpdateUserRole"));
        } finally {
            setUpdatingRoleUserId(null);
        }
    };

    const handleObfuscationToggle = async (
        userIdToUpdate,
        currentRole,
        newValue
    ) => {
        setUpdatingRoleUserId(userIdToUpdate);
        try {
            await updateUserRole({
                userIdToUpdate,
                newRole: currentRole,
                canModifyObfuscation: newValue,
            });
            toast.success(
                newValue
                    ? t("users.canModifyObfuscationEnabled")
                    : t("users.canModifyObfuscationDisabled")
            );
            getUsers({
                search: debouncedSearch,
                limit: pageSize,
                offset: (currentPage - 1) * pageSize,
                companyOnly: actAsAdmin || undefined,
            });
        } catch (err) {
            toast.error(err?.message || t("users.failedToUpdateUserRole"));
        } finally {
            setUpdatingRoleUserId(null);
        }
    };

    const canManage = isAdmin || isSuperAdmin || isSupport;

    const canDeleteUser = (user) => {
        return canManage && user.id !== currentUser?.id;
    };

    const canEditRole = (user) => {
        if (!canManage || !user?.id || !currentUser?.id) {
            return false;
        }
        // Use strict comparison and handle potential type mismatches (string vs number)
        return String(user.id) !== String(currentUser.id);
    };

    const toggleUserExpansion = (userId) => {
        setExpandedUsers((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchQuery(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
        }, 300);
    }, []);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const totalPages = Math.ceil((users?.pagination?.total || 0) / pageSize);

    if (error) {
        return (
            <div className="p-4 text-red-500">
                {t("users.errorLoadingUsers", { message: error.message })}
            </div>
        );
    }

    const getRoleIcon = (role) => {
        switch (role) {
            case "admin":
                return <Shield className="w-4 h-4 text-red-500" />;
            case "creator":
                return <User className="w-4 h-4 text-blue-500" />;
            case "client":
                return <User className="w-4 h-4 text-green-500" />;
            case "compliance-officer":
                return <Shield className="w-4 h-4 text-purple-500" />;
            default:
                return <User className="w-4 h-4 text-gray-500" />;
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case "admin":
                return "bg-red-100 text-red-800";
            case "creator":
                return "bg-blue-100 text-blue-800";
            case "client":
                return "bg-green-100 text-green-800";
            case "compliance-officer":
                return "bg-purple-100 text-purple-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="font-semibold text-xl">{t("users.title")}</h2>
                {canManage && <DialogNewUser onSuccess={handleUserCreated} />}
            </div>

            {/* Search Bar */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="pl-10"
                    />
                </div>
                {users?.pagination && (
                    <div className="text-sm text-gray-600">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(
                            currentPage * pageSize,
                            users.pagination.total
                        )}{" "}
                        of {users.pagination.total} users
                    </div>
                )}
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[100px]">
                            {t("users.id")}
                        </TableHead>
                        <TableHead>{t("users.email")}</TableHead>
                        <TableHead>{t("users.displayName")}</TableHead>
                        <TableHead>{t("users.role")}</TableHead>
                        <TableHead>{t("users.createdDate")}</TableHead>
                        {canManage && (
                            <TableHead className="w-[100px]">
                                {t("users.actions")}
                            </TableHead>
                        )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading
                        ? // Loading skeleton
                          [...Array(3)].map((_, index) => (
                              <TableRow key={`loading-${index}`}>
                                  <TableCell>
                                      <Skeleton className="h-4 w-8" />
                                  </TableCell>
                                  <TableCell>
                                      <Skeleton className="h-4 w-[200px]" />
                                  </TableCell>
                                  <TableCell>
                                      <Skeleton className="h-4 w-[150px]" />
                                  </TableCell>
                                  <TableCell>
                                      <Skeleton className="h-4 w-[100px]" />
                                  </TableCell>
                                  <TableCell>
                                      <Skeleton className="h-4 w-[100px]" />
                                  </TableCell>
                                  {canManage && (
                                      <TableCell>
                                          <Skeleton className="h-4 w-[80px]" />
                                      </TableCell>
                                  )}
                              </TableRow>
                          ))
                        : users?.users?.map((user) => (
                              <>
                                  <TableRow key={user.id}>
                                      <TableCell className="font-medium">
                                          {user.id}
                                      </TableCell>
                                      <TableCell>{user.email}</TableCell>
                                      <TableCell>
                                          <div className="flex items-center gap-2">
                                              {user.companies &&
                                                  user.companies.length > 0 && (
                                                      <button
                                                          onClick={() =>
                                                              toggleUserExpansion(
                                                                  user.id
                                                              )
                                                          }
                                                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                          title={
                                                              expandedUsers.has(
                                                                  user.id
                                                              )
                                                                  ? "Collapse companies"
                                                                  : "Expand companies"
                                                          }
                                                      >
                                                          {expandedUsers.has(
                                                              user.id
                                                          ) ? (
                                                              <ChevronDown className="w-4 h-4 text-gray-500" />
                                                          ) : (
                                                              <ChevronRight className="w-4 h-4 text-gray-500" />
                                                          )}
                                                      </button>
                                                  )}
                                              <div className="flex items-center gap-2">
                                                  {user.displayName}
                                                  {user.id ===
                                                      currentUser?.id && (
                                                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                          {t("users.you")}
                                                      </span>
                                                  )}
                                              </div>
                                          </div>
                                      </TableCell>
                                      <TableCell>
                                          <div className="flex items-center gap-2">
                                              {canEditRole(user) ? (
                                                  <Select
                                                      value={user.department}
                                                      onValueChange={(
                                                          newRole
                                                      ) =>
                                                          handleRoleChange(
                                                              user.id,
                                                              newRole
                                                          )
                                                      }
                                                      disabled={
                                                          updatingRoleUserId ===
                                                          user.id
                                                      }
                                                  >
                                                      <SelectTrigger className="w-[180px]">
                                                          <div className="flex items-center gap-2">
                                                              {getRoleIcon(
                                                                  user.department
                                                              )}
                                                              <SelectValue />
                                                          </div>
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                          <SelectItem value="admin">
                                                              <div className="flex items-center gap-2">
                                                                  {t(
                                                                      "users.admin"
                                                                  )}
                                                              </div>
                                                          </SelectItem>
                                                          <SelectItem value="creator">
                                                              <div className="flex items-center gap-2">
                                                                  {t(
                                                                      "users.creator"
                                                                  )}
                                                              </div>
                                                          </SelectItem>
                                                          <SelectItem value="client">
                                                              <div className="flex items-center gap-2">
                                                                  {t(
                                                                      "users.client"
                                                                  )}
                                                              </div>
                                                          </SelectItem>
                                                          {hasComplianceOfficer && (
                                                              <SelectItem value="compliance-officer">
                                                                  <div className="flex items-center gap-2">
                                                                      {t(
                                                                          "users.complianceOfficer"
                                                                      )}
                                                                  </div>
                                                              </SelectItem>
                                                          )}
                                                      </SelectContent>
                                                  </Select>
                                              ) : (
                                                  <span
                                                      className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getRoleBadgeColor(
                                                          user.department
                                                      )}`}
                                                  >
                                                      {getRoleIcon(
                                                          user.department
                                                      )}
                                                      {user.department}
                                                  </span>
                                              )}
                                              {hasComplianceOfficer &&
                                                  canEditRole(user) &&
                                                  (user.department ===
                                                      "creator" ||
                                                      user.department ===
                                                          "compliance-officer") && (
                                                      <Tooltip>
                                                          <TooltipTrigger
                                                              asChild
                                                          >
                                                              <div className="flex items-center gap-1">
                                                                  <EyeOff className="w-3 h-3 text-gray-500" />
                                                                  <Switch
                                                                      checked={
                                                                          user
                                                                              .companies?.[0]
                                                                              ?.canModifyObfuscation !==
                                                                          false
                                                                      }
                                                                      onCheckedChange={(
                                                                          val
                                                                      ) =>
                                                                          handleObfuscationToggle(
                                                                              user.id,
                                                                              user.department,
                                                                              val
                                                                          )
                                                                      }
                                                                      disabled={
                                                                          updatingRoleUserId ===
                                                                          user.id
                                                                      }
                                                                      className="scale-75"
                                                                  />
                                                              </div>
                                                          </TooltipTrigger>
                                                          <TooltipContent>
                                                              {t(
                                                                  "users.canModifyObfuscationDescription"
                                                              )}
                                                          </TooltipContent>
                                                      </Tooltip>
                                                  )}
                                          </div>
                                      </TableCell>
                                      <TableCell>{user.createdDate}</TableCell>
                                      {canManage && (
                                          <TableCell>
                                              {canDeleteUser(user) && (
                                                  <AlertDialog>
                                                      <AlertDialogTrigger
                                                          asChild
                                                      >
                                                          <Button
                                                              variant="ghost"
                                                              size="sm"
                                                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                              disabled={
                                                                  deletingUserId ===
                                                                  user.id
                                                              }
                                                          >
                                                              <Trash2 className="w-4 h-4" />
                                                          </Button>
                                                      </AlertDialogTrigger>
                                                      <AlertDialogContent>
                                                          <AlertDialogHeader>
                                                              <AlertDialogTitle>
                                                                  {t(
                                                                      "users.deleteUser"
                                                                  )}
                                                              </AlertDialogTitle>
                                                              <AlertDialogDescription>
                                                                  {t(
                                                                      "users.deleteUserDescription",
                                                                      {
                                                                          displayName:
                                                                              user.firstName +
                                                                              " " +
                                                                              user.lastName,
                                                                      }
                                                                  )}
                                                              </AlertDialogDescription>
                                                          </AlertDialogHeader>
                                                          <AlertDialogFooter>
                                                              <AlertDialogCancel>
                                                                  {t(
                                                                      "users.cancel"
                                                                  )}
                                                              </AlertDialogCancel>
                                                              <AlertDialogAction
                                                                  onClick={() =>
                                                                      handleDeleteUser(
                                                                          user.id
                                                                      )
                                                                  }
                                                                  className="bg-red-600 hover:bg-red-700"
                                                              >
                                                                  {t(
                                                                      "users.deleteUser"
                                                                  )}
                                                              </AlertDialogAction>
                                                          </AlertDialogFooter>
                                                      </AlertDialogContent>
                                                  </AlertDialog>
                                              )}
                                          </TableCell>
                                      )}
                                  </TableRow>
                                  {user.companies &&
                                      user.companies.length > 0 &&
                                      expandedUsers.has(user.id) && (
                                          <TableRow
                                              key={`${user.id}-companies`}
                                              className="bg-gray-50/50"
                                          >
                                              <TableCell
                                                  colSpan={canManage ? 6 : 5}
                                                  className="py-2"
                                              >
                                                  <div className="flex items-start gap-2 pl-6">
                                                      <Building2 className="w-4 h-4 mt-0.5 text-gray-500" />
                                                      <div className="flex-1">
                                                          <div className="text-xs font-medium text-gray-600 mb-1">
                                                              Companies:
                                                          </div>
                                                          <div className="flex flex-wrap gap-2">
                                                              {user.companies.map(
                                                                  (
                                                                      companyData
                                                                  ) => (
                                                                      <div
                                                                          key={
                                                                              companyData
                                                                                  .company
                                                                                  .id
                                                                          }
                                                                          className="flex items-center gap-1"
                                                                      >
                                                                          <span
                                                                              className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                                                                                  companyData.isActive
                                                                                      ? "bg-blue-100 text-blue-800 font-medium"
                                                                                      : "bg-gray-100 text-gray-700"
                                                                              }`}
                                                                          >
                                                                              <span>
                                                                                  {
                                                                                      companyData
                                                                                          .company
                                                                                          .name
                                                                                  }
                                                                              </span>
                                                                              <span className="text-gray-400">
                                                                                  •
                                                                              </span>
                                                                              <span className="capitalize">
                                                                                  {
                                                                                      companyData.role
                                                                                  }
                                                                              </span>
                                                                              {companyData.isActive && (
                                                                                  <span className="ml-1 text-[10px]">
                                                                                      (Active)
                                                                                  </span>
                                                                              )}
                                                                          </span>
                                                                      </div>
                                                                  )
                                                              )}
                                                          </div>
                                                      </div>
                                                  </div>
                                              </TableCell>
                                          </TableRow>
                                      )}
                              </>
                          ))}
                </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </Button>

                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                            {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={
                                                currentPage === pageNum
                                                    ? "default"
                                                    : "outline"
                                            }
                                            size="sm"
                                            onClick={() =>
                                                handlePageChange(pageNum)
                                            }
                                            className="w-8 h-8 p-0"
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                }
                            )}
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                            <ChevronRightIcon className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableUsers;
